import { Button, Popconfirm, Table } from "@/frontend/components/common";
import { tournamentService, disciplineService, tournamentStatusService } from "@/frontend/services";
import type { PaginationRequestDTO, FlatPaginationRequestDTO, TournamentResponseDTO, DisciplineResponseDTO, TournamentStatusResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaCog } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useDelete, usePagination } from "@/frontend/hooks";
import { useMemo, useState, useEffect } from "react";
import { DatePicker, Input, Select } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: FlatPaginationRequestDTO = {
  page: 1,
  pageSize: 7,
};

export function TournamentListPage() {
  const navigate = useNavigate();
  const { data, loading, pagination, loadData } = usePagination<TournamentResponseDTO>({
    fetchFn: tournamentService.getPaginate,
    initialParams: initialParams,
    initialPagination: initialPagination,
  });

  const [search, setSearch] = useState("");
  const [filterDisciplineId, setFilterDisciplineId] = useState<string | undefined>();
  const [filterStatusId, setFilterStatusId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [statuses, setStatuses] = useState<TournamentStatusResponseDTO[]>([]);

  useEffect(() => {
    disciplineService.getPaginate({ page: 1, pageSize: 100 }).then((res) => {
      if (res.success && res.data) setDisciplines(res.data.data);
    });
    tournamentStatusService.getPaginate({ page: 1, pageSize: 100 }).then((res) => {
      if (res.success && res.data) setStatuses(res.data.data);
    });
  }, []);

  function applyFilters(name?: string, disciplineId?: string, range?: [string, string] | null, statusId?: string) {
    loadData({
      page: 1,
      pageSize: 7,
      name,
      disciplineId,
      statusId,
      seasonStartFrom: range?.[0],
      seasonStartTo: range?.[1],
    });
  }

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } =
    useDelete({
      deleteFn: tournamentService.delete,
      loadData: () => {},
      successMessage: "Torneo eliminado exitosamente",
      errorMessage: "Error al eliminar torneo",
      showConfirm: true,
      confirmTitle: "¿Eliminar torneo?",
      confirmDescription: "Esta acción eliminará el torneo. ¿Estás seguro?",
    });

  const enrichedData = useMemo(() => {
    return data.map((r) => {
      const current = r.seasons?.find((s) => s.isCurrent);

      return {
        ...r,
        __seasonStart: current?.start,
        __seasonEnd: current?.end,
        __seasonName: current?.season,
        __seasonStatus: current?.status?.name,
        __seasonFormat: current?.format?.name,
      } as TournamentResponseDTO & {
        __seasonStart?: Date;
        __seasonEnd?: Date;
        __seasonName?: string;
        __seasonStatus?: string;
        __seasonFormat?: string;
      };
    });
  }, [data]);

  const columns: ColumnsType<
    TournamentResponseDTO & {
      __seasonStart?: Date;
      __seasonEnd?: Date;
      __seasonStatus?: string;
    }
  > = [
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      width: 60,
      className: "text-center",
      render: (logo) => {
        if (logo) {
          return (
            <img
              src={logo}
              alt="Logo torneo"
              className="h-6 w-6 object-contain mx-auto"
            />
          );
        }
        return null;
      },
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      className: "text-center",
    },
    {
      title: "Disciplina",
      key: "discipline",
      render: (_: any, record: TournamentResponseDTO) =>
        record.discipline?.name || record.type || "",
    },
    {
      title: "Temporada",
      key: "season",
      render: (
        _: any,
        record: TournamentResponseDTO & { __seasonName?: string },
      ) => {
        return (
          <span className={"text-gray-600"}>
            {record.__seasonName ?? "Sin temporada"}
          </span>
        );
      },
    },
    {
      title: "Formato",
      key: "format",
      render: (
        _: any,
        record: TournamentResponseDTO & { __seasonFormat?: string },
      ) => {
        return (
          <span className={"text-gray-600"}>
            {record.__seasonFormat ?? "-"}
          </span>
        );
      },
    },
    {
      title: "Estado",
      key: "status",
      render: (
        _: any,
        record: TournamentResponseDTO & { __seasonStatus?: string },
      ) => {
        return (
          <span className={"text-gray-600"}>
            {record.__seasonStatus ?? "Sin estado"}
          </span>
        );
      },
    },
    {
      title: "Fecha de Inicio",
      key: "startDate",
      render: (
        _: any,
        record: TournamentResponseDTO & { __seasonStart?: Date },
      ) => formatDate(record.__seasonStart),
    },
    {
      title: "Fecha de Fin",
      key: "endDate",
      render: (
        _: any,
        record: TournamentResponseDTO & { __seasonEnd?: Date },
      ) => formatDate(record.__seasonEnd),
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 160,
      render: (_: any, record: TournamentResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaCog />}
            onClick={() => navigate(`/tournaments/${record.id}`)}
          >
            Configuración
          </Button>
          {record.canDelete &&
            (showConfirm ? (
              <Popconfirm
                title={confirmTitle}
                description={confirmDescription}
                onConfirm={() => handleDelete(record.id)}
                okText="Sí, eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
              >
                <Button type="link" danger>
                  Eliminar
                </Button>
              </Popconfirm>
            ) : (
              <Button
                type="link"
                danger
                onClick={() => handleDelete(record.id)}
              >
                Eliminar
              </Button>
            ))}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Gestión de Torneos
          </h2>
          <p className="text-gray-600 mt-1">
            Administra los torneos de la plataforma
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/tournaments/create")}
        >
          Crear Torneo
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <Input
          placeholder="Buscar por nombre"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => applyFilters(search, filterDisciplineId, dateRange, filterStatusId)}
          style={{ width: 220 }}
          allowClear
          onClear={() => { setSearch(""); applyFilters("", filterDisciplineId, dateRange, filterStatusId); }}
        />

        <Select
          placeholder="Disciplina"
          allowClear
          value={filterDisciplineId}
          onChange={(val) => { setFilterDisciplineId(val); applyFilters(search, val, dateRange, filterStatusId); }}
          options={disciplines.map((d) => ({ label: d.name, value: d.id }))}
          style={{ width: 180 }}
          showSearch
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />

        <RangePicker
          placeholder={["Fecha inicio desde", "Fecha inicio hasta"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(search, filterDisciplineId, range, filterStatusId);
          }}
        />

        <Select
          placeholder="Estado"
          allowClear
          value={filterStatusId}
          onChange={(val) => { setFilterStatusId(val); applyFilters(search, filterDisciplineId, dateRange, val); }}
          options={statuses.map((s) => ({ label: s.name, value: s.id }))}
          style={{ width: 180 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={enrichedData}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
